// hooks/useApplication.js
import { useState, useEffect, useCallback } from 'react';
import { applicationApi, fileApi } from '../api/applicationApi';
import { mapApplicationToComponentData } from '../utils/applicationDataMapper';


export const useApplication = (applicationId) => {
  const [application, setApplication] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [statusesLoading, setStatusesLoading] = useState(false);
  const [statusesError, setStatusesError] = useState(null);

  const fetchApplication = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await applicationApi.getApplicationById(applicationId);
      const mappedData = mapApplicationToComponentData(data);
      setApplication(mappedData);
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка завантаження заявки');
      console.error('Error fetching application:', err);
    } finally {
      setLoading(false);
    }
  }, [applicationId]);

  const fetchApplicationStatuses = useCallback(async () => {
    try {
      setStatusesLoading(true);
      setStatusesError(null);
      const data = await applicationApi.getApplicationStatuses(applicationId);
      setStatuses(data);
      return data;
    } catch (err) {
      setStatusesError(err.response?.data?.message || 'Помилка завантаження статусів');
      console.error('Error fetching application statuses:', err);
      throw err;
    } finally {
      setStatusesLoading(false);
    }
  }, [applicationId]);

  const updateApplicationStatus = useCallback(async (statusData) => {
    try {
      const updatedApplication = await applicationApi.updateApplicationStatus(applicationId, statusData);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка оновлення статусу');
      throw err;
    }
  }, [applicationId]);

  const verifyRegistration = useCallback(async (comment = '') => {
    try {
      const updatedApplication = await applicationApi.verifyRegistration(applicationId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка підтвердження реєстрації');
      throw err;
    }
  }, [applicationId]);

  const verifyBenefit = useCallback(async (benefitId, comment = '') => {
    try {
      const updatedApplication = await applicationApi.verifyBenefit(applicationId, benefitId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка підтвердження пільги');
      throw err;
    }
  }, [applicationId]);

  // Оновлення даних пільги модератором
  const updateBenefits = useCallback(async (benefitsData) => {
    try {
      const updatedApplication = await applicationApi.updateBenefits(applicationId, benefitsData);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка збереження пільги');
      throw err;
    }
  }, [applicationId]);

  // Збереження пільги з обробкою файлів (завантаження нових + видалення видалених)
  const saveBenefitsWithFiles = useCallback(async (newBenefits, originalBenefits) => {
    try {
      // FileUploader повертає об'єкти двох типів:
      //   - існуючий (з сервера): { _id, type, file: { name, mime, size, ... } }
      //   - новий (доданий):      { type, name, size, file: "data:image/...;base64,..." }
      // Новий визначається відсутністю _id І наявністю file як рядка (base64)

      const isNewFile = (f) => !f._id && typeof f.file === 'string';
      const getFileId = (f) => f._id || f.id;

      // 1. Файли для видалення: були в originalBenefits.documents, відсутні в новому benScan
      const filesToDelete = [];
      (originalBenefits || []).forEach(origBenefit => {
        (origBenefit.documents || []).forEach(doc => {
          const docId = doc.id; // mapped _id
          const stillExists = newBenefits.some(nb =>
            (nb.benScan || []).some(f => getFileId(f) === docId)
          );
          if (!stillExists && docId) {
            filesToDelete.push(docId);
          }
        });
      });

      // 2. Нові файли для завантаження
      const filesToUpload = [];
      newBenefits.forEach(nb => {
        (nb.benScan || []).forEach(f => {
          if (isNewFile(f)) {
            // передаємо весь об'єкт — sendFileAsBinaryPOST читає .file як тіло
            filesToUpload.push(f);
          }
        });
      });

      // 3. Видаляємо файли
      for (const fileId of filesToDelete) {
        await applicationApi.deleteFile(applicationId, fileId, 'benefits');
      }

      // 4. Завантажуємо нові файли
      for (const fileObj of filesToUpload) {
        await applicationApi.uploadBenefitFile(applicationId, fileObj);
      }

      // 5. Зберігаємо дані пільги.
      //    benScan — тільки існуючі { _id }, нові сервер вже прийняв вище
      const benefitsPayload = newBenefits.map(nb => ({
        ...nb,
        benScan: (nb.benScan || [])
          .filter(f => !isNewFile(f) && getFileId(f))
          .map(f => ({ _id: getFileId(f) }))
      }));

      const updatedApplication = await applicationApi.updateBenefits(applicationId, benefitsPayload);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка збереження пільги');
      throw err;
    }
  }, [applicationId]);

  // Автоматична перевірка реєстрації через реєстр
  const checkMR = useCallback(async (childData) => {
    try {
      const data = {
        fName: childData.firstName?.trim(),
        lName: childData.lastName?.trim(),
        birthDate: childData.birthDate?.split('T')[0],
        birthCert: childData.birthCertificateNumber,
      };
      const result = await applicationApi.checkMR(applicationId, data);
      return result;
    } catch (err) {
      throw err;
    }
  }, [applicationId]);

  // Ручне встановлення реєстрації (status: true = підтвердити, false = скасувати)
  const setMR = useCallback(async (childData, status) => {
    try {
      const data = {
        fName: childData.firstName?.trim(),
        birthDate: childData.birthDate?.split('T')[0],
        birthCert: childData.birthCertificateNumber,
        status: status ? 'true' : 'false',
      };
      const result = await applicationApi.setMR(applicationId, data);
      // Перезавантажуємо заявку щоб оновити statusMR
      await fetchApplication();
      return result;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка оновлення реєстрації');
      throw err;
    }
  }, [applicationId, fetchApplication]);

  // Скасування пільги модератором
  const declineBenefit = useCallback(async (description) => {
    try {
      const updatedApplication = await applicationApi.declineBenefit(applicationId, description);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка скасування пільги');
      throw err;
    }
  }, [applicationId]);

  const rejectApplication = useCallback(async (reason, comment = '') => {
    try {
      const updatedApplication = await applicationApi.rejectApplication(applicationId, reason, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відхилення заявки');
      throw err;
    }
  }, [applicationId]);

  const restoreApplication = useCallback(async (comment = '') => {
    try {
      const updatedApplication = await applicationApi.restoreApplication(applicationId, comment);
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відновлення заявки');
      throw err;
    }
  }, [applicationId]);

  const declineInvitation = useCallback(async (reason, kindergartenId, groupNumber) => {
    try {
      const updatedApplication = await applicationApi.declineInvitation(
        applicationId, 
        reason, 
        kindergartenId, 
        groupNumber
      );
      const mappedData = mapApplicationToComponentData(updatedApplication);
      setApplication(mappedData);
      return mappedData;
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка відхилення запрошення');
      throw err;
    }
  }, [applicationId]);

  const downloadFile = useCallback(async (doc_id, fileId, path, fileName) => {
    try {
      await fileApi.downloadFile(doc_id, fileId, path, fileName);
    } catch (err) {
      setError(err.response?.data?.message || 'Помилка завантаження файлу');
      throw err;
    }
  }, []);


  useEffect(() => {
    if (applicationId) {
      fetchApplication();
    }
  }, [applicationId, fetchApplication]);

  return {
    application,
    loading,
    error,
    statuses,
    statusesLoading,
    statusesError,
    refetch: fetchApplication,
    fetchApplicationStatuses,
    updateApplicationStatus,
    verifyRegistration,
    checkMR,
    setMR,
    verifyBenefit,
    updateBenefits,
    saveBenefitsWithFiles,
    declineBenefit,
    rejectApplication,
    restoreApplication,
    declineInvitation,
    downloadFile
  };
};